import { useEffect, useState } from 'react';
import { cleanDocumentHTML, logTermsAcceptance, TERMS_DOCUMENT_URL_FETCH_TIMEOUT_MS } from '@ibk/shared-utils';
async function fetchLinkedTermsDocument(url, signal) {
    try {
        const res = await fetch(url, {
            credentials: 'omit',
            mode: 'cors',
            signal,
        });
        if (!res.ok) {
            return { kind: 'fallback', aborted: false, error: new Error(`HTTP ${res.status}`) };
        }
        const raw = await res.text();
        return { kind: 'html', html: cleanDocumentHTML(raw) };
    }
    catch (error) {
        const aborted = error instanceof Error && error.name === 'AbortError';
        return { kind: 'fallback', aborted, error };
    }
}
function logLinkedTermsFetchFallback(aborted, error) {
    if (typeof process !== 'undefined' && process.env.NODE_ENV === 'test') {
        return;
    }
    logTermsAcceptance(aborted ? 'modal.document_fetch_timeout_or_abort' : 'modal.document_fetch_iframe_fallback', {
        description: aborted
            ? 'Timeout ou abort ao buscar documento por URL; exibindo iframe.'
            : 'Não foi possível carregar o documento como HTML; usando iframe.',
    }, 'warn', aborted ? undefined : error);
}
function applyLinkedFetchOutcome(outcome, setLinkedTerms) {
    if (outcome.kind === 'html') {
        setLinkedTerms({ status: 'html', html: outcome.html });
        return;
    }
    logLinkedTermsFetchFallback(outcome.aborted, outcome.error);
    setLinkedTerms({ status: 'iframe' });
}
async function loadLinkedTermsDocument(url, signal, timeoutId, isCancelled, setLinkedTerms) {
    try {
        const outcome = await fetchLinkedTermsDocument(url, signal);
        if (isCancelled()) {
            return;
        }
        applyLinkedFetchOutcome(outcome, setLinkedTerms);
    }
    finally {
        clearTimeout(timeoutId);
    }
}
export function useLinkedTermsFromUrl(isOpen, termsDocumentUrl, children) {
    const [linkedTerms, setLinkedTerms] = useState({ status: 'idle' });
    const hasCustomTermsBody = children != null;
    useEffect(() => {
        if (!isOpen || !termsDocumentUrl || hasCustomTermsBody) {
            setLinkedTerms({ status: 'idle' });
            return;
        }
        let cancelled = false;
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), TERMS_DOCUMENT_URL_FETCH_TIMEOUT_MS);
        setLinkedTerms({ status: 'loading' });
        void loadLinkedTermsDocument(termsDocumentUrl, controller.signal, timeoutId, () => cancelled, setLinkedTerms);
        return () => {
            cancelled = true;
            clearTimeout(timeoutId);
            controller.abort();
        };
    }, [isOpen, termsDocumentUrl, hasCustomTermsBody]);
    return linkedTerms;
}
